"use client";

import { useEffect, useRef } from "react";
import AppImage from "@/components/ui/AppImage";

const services = [
  {
    id: "pilates-classico",
    title: "Pilates",
    subtitle: "Método Clássico",
    description: "O método Pilates original, desenvolvido por Joseph Pilates, trabalha o corpo de forma holística com foco no core, postura e respiração. Ideal para qualquer nível, desde iniciantes até praticantes avançados.",
    benefits: [
      "Melhora da postura e alinhamento corporal",
      "Fortalecimento profundo da musculatura central",
      "Aumento da flexibilidade e mobilidade articular",
      "Prevenção e reabilitação de lesões",
      "Redução de dores crônicas na coluna",
      "Consciência corporal ampliada",
    ],
    for: ["Iniciantes", "Reabilitação", "Todos os níveis"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_113e9e82c-1767635574768.png",
    alt: "Aluna no reformer de Pilates com instrutora em estúdio moderno e bem iluminado",
    sessions: "Turmas de até 4 alunos",
    duration: "50 minutos",
    number: "01",
  },
  {
    id: "pilates-funcional",
    title: "Pilates Funcional",
    subtitle: "Movimento Integrado",
    description: "A fusão do Pilates clássico com movimentos funcionais modernos. Esta modalidade trabalha padrões de movimento do cotidiano, melhorando a força, o equilíbrio e a coordenação motora de forma dinâmica.",
    benefits: [
      "Ganho de força funcional aplicada ao dia a dia",
      "Melhora do equilíbrio e propriocepção",
      "Desenvolvimento da coordenação motora",
      "Prevenção de quedas e lesões esportivas",
      "Condicionamento físico progressivo",
      "Integração mente-corpo-movimento",
    ],
    for: ["Intermediário", "Esportistas", "Adultos ativos"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1861a54e7-1772741696284.png",
    alt: "Aula de Pilates funcional com exercícios de equilíbrio sobre superfície instável",
    sessions: "Turmas de até 6 alunos",
    duration: "55 minutos",
    number: "02",
  },
  {
    id: "circuito-funcional",
    title: "Circuito Funcional",
    subtitle: "Alta Performance",
    description: "Treino em circuito que combina estações de resistência, agilidade e condicionamento cardiovascular com a precisão e segurança dos princípios do Pilates. Máximo resultado em tempo otimizado.",
    benefits: [
      "Condicionamento cardiovascular eficiente",
      "Queima calórica elevada e segura",
      "Desenvolvimento de agilidade e velocidade",
      "Fortalecimento muscular completo",
      "Melhora da resistência física",
      "Resultados visíveis em 4 a 6 semanas",
    ],
    for: ["Avançado", "Alto desempenho", "Emagrecimento"],
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9d3b480-1772741701649.png",
    alt: "Circuito funcional com múltiplas estações de exercícios em estúdio amplo",
    sessions: "Turmas de até 8 alunos",
    duration: "60 minutos",
    number: "03",
  },
];

export default function ServicesList() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.remove("reveal-hidden");
            e.target.classList.add("reveal-active");
          }
        });
      },
      { threshold: 0.06 }
    );
    ref?.current?.querySelectorAll(".reveal-hidden")?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section className="py-32 md:py-44 px-6 md:px-10 bg-cream" ref={ref}>
      <div className="max-w-8xl mx-auto space-y-40">
        {services?.map((service, i) => (
          <div
            key={service?.id}
            className={`grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center ${i % 2 === 1 ? "lg:grid-flow-col-dense" : ""}`}
          >
            {/* Image */}
            <div className={`reveal-hidden ${i % 2 === 1 ? "lg:order-2" : ""}`}>
              <div className="relative rounded-5xl overflow-hidden aspect-[4/3]" style={{ boxShadow: '0 20px 80px rgba(26,61,26,0.18)' }}>
                <AppImage
                  src={service?.image}
                  alt={service?.alt}
                  fill
                  className="object-cover object-center"
                />
                {/* Overlay badges */}
                <div className="absolute bottom-6 left-6 flex gap-2">
                  <div className="glass-card rounded-xl px-3 py-1.5">
                    <p className="text-xs font-medium" style={{ color: 'var(--charcoal)' }}>{service?.sessions}</p>
                  </div>
                  <div className="glass-card rounded-xl px-3 py-1.5">
                    <p className="text-xs font-medium" style={{ color: 'var(--charcoal)' }}>{service?.duration}</p>
                  </div>
                </div>
                {/* Decorative number */}
                <div className="absolute top-6 right-6 font-serif font-light leading-none select-none"
                  style={{ fontSize: '6rem', color: 'rgba(255,255,255,0.12)' }}>
                  {service?.number}
                </div>
              </div>
            </div>

            {/* Content */}
            <div className={`reveal-hidden reveal-delay-2 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
              <span className="subheading mb-3 block">{service?.subtitle}</span>
              <h2 className="font-serif text-forest leading-tight mb-8"
                style={{ fontSize: 'clamp(2.2rem, 4vw, 3.2rem)' }}>
                {service?.title}
              </h2>
              <p className="leading-relaxed mb-10" style={{ color: 'var(--stone)', fontSize: '1.05rem' }}>
                {service?.description}
              </p>

              {/* Benefits */}
              <div className="mb-10">
                <p className="text-xs uppercase tracking-widest mb-5 font-semibold" style={{ color: 'var(--stone-light)' }}>
                  Benefícios
                </p>
                <ul className="space-y-3">
                  {service?.benefits?.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3 text-sm" style={{ color: 'var(--charcoal)' }}>
                      <span className="w-1.5 h-1.5 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--gold)' }} />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              {/* For who */}
              <div className="flex flex-wrap gap-2 mb-10">
                {service?.for?.map((tag) => (
                  <span key={tag} className="text-xs px-4 py-1.5 rounded-full font-medium" style={{ background: 'var(--sage-pale)', color: 'var(--forest-mid)' }}>
                    {tag}
                  </span>
                ))}
              </div>

              <div className="divider-gold mb-10" />

              <a
                href={`https://wa.me/5551999999999?text=Olá! Tenho interesse em ${service?.title}. Gostaria de agendar uma aula experimental.`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                Agendar Aula Experimental
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
